import React, { useEffect, useState } from 'react';

import { Box, Col, Row } from '@opendoor/atoms/box';
import { Text } from '@opendoor/atoms/text';
import { cn } from '@opendoor/atoms/utils';
import { useAuth } from '@opendoor/auth-fe/context';
import { COSMOS_URL } from 'components/globals';

import { OpendoorLogo } from '../../OpendoorLogo';
import type { MenuConfig, NavigationProps } from '../../types';
import { MobileMenuAccountItemsV2 } from '../mobile/MobileMenuAccountItems';
import { MobileMenuList } from '../mobile/MobileMenuList';
import { MobileMenuSubMenu } from '../mobile/MobileMenuSubMenu';
import { MenuCTA } from './MenuCTA';

import styles from './MenuContent.module.css';

interface MenuContentProps extends NavigationProps, MenuConfig {
  isVisible: boolean;
  containerRef: React.RefObject<HTMLDivElement>;
  onAnimationComplete: (definition: 'visible' | 'hidden') => void;
  variant?: 'default' | 'glass';
  onClose: () => void;
}

export const MenuContent: React.FC<MenuContentProps> = ({
  isVisible,
  containerRef,
  onAnimationComplete,
  onClose,
  slug,
  menuPanelNavLinks,
  mainNavLinks,
  ctaLink,
  ctaTitle,
  megaNavData,
  variant = 'default',
}) => {
  const { authentication } = useAuth();
  const isLoggedIn = authentication.state === 'authenticated';

  const accountItems: React.ReactNode = isLoggedIn ? (
    <MobileMenuAccountItemsV2 slug={slug} />
  ) : null;

  const [openSubMenu, setOpenSubMenu] = useState<string | null>(null);
  // Keep the most recently opened submenu mounted so it can slide back out.
  const [activeSubMenu, setActiveSubMenu] = useState<string | null>(null);
  useEffect(() => {
    if (openSubMenu) {
      // oxlint-disable-next-line react/set-state-in-effect -- react19-baseline: pre-existing, do not copy. Derive during render or set from the event instead
      setActiveSubMenu(openSubMenu);
    }
  }, [openSubMenu]);

  // Slide the panel in on the frame after mount; slide out is driven by `isVisible`.
  const [entered, setEntered] = useState(false);
  useEffect(() => {
    let inner = 0;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => setEntered(true));
    });
    return () => {
      cancelAnimationFrame(outer);
      cancelAnimationFrame(inner);
    };
  }, []);

  const open = isVisible && entered;
  const showSubMenu = Boolean(megaNavData && openSubMenu);

  const handleTransitionEnd = (event: React.TransitionEvent<HTMLElement>) => {
    if (
      event.target !== event.currentTarget ||
      (event.propertyName !== 'transform' && event.propertyName !== 'translate')
    ) {
      return;
    }
    onAnimationComplete(open ? 'visible' : 'hidden');
  };

  const handleSignIn = () => {
    document.location.href = `${COSMOS_URL}/login?redirectTo=${encodeURIComponent(
      window.location.pathname,
    )}`;
  };

  const signInContent =
    authentication.state === 'not_authenticated' ? (
      <Col>
        <Box
          onClick={handleSignIn}
          aria-label="Sign in to your account"
          analyticsName="cosmos-mobile-menu-sign-in"
          className="inline-flex h-11 w-full cursor-pointer items-center justify-center rounded-xl border-0 bg-white/90 px-6 font-medium text-od-content-primary shadow-[0_0_0_1px_rgb(88_64_50_/_0.16),inset_0_0_0_1px_rgb(255_255_255_/_0.3),inset_0_-4px_0_0_rgb(88_64_50_/_0.1)]"
        >
          <Text as="span" className="text-[16px] font-medium tracking-[-0.8px] text-inherit">
            Sign in
          </Text>
        </Box>
      </Col>
    ) : null;

  const menuFooter =
    signInContent || (ctaLink?.name && ctaLink.href) ? (
      <Col className="px-2 py-5">
        {signInContent}
        <MenuCTA ctaLink={ctaLink} ctaTitle={ctaTitle} open={open} />
      </Col>
    ) : null;

  const mainMenu = (
    <MobileMenuList
      megaNavData={megaNavData}
      menuPanelNavLinks={menuPanelNavLinks}
      mainNavLinks={mainNavLinks}
      slug={slug}
      onSubMenuOpen={setOpenSubMenu}
      accountItems={accountItems}
      footer={menuFooter}
    />
  );

  return (
    <Box
      ref={containerRef}
      tabIndex={-1}
      className={cn(
        styles.menuContainer,
        'flex-col transition-transform duration-500 ease-out',
        open ? 'translate-x-0' : 'translate-x-full',
      )}
      data-variant={variant}
      role="dialog"
      aria-label="Navigation"
      aria-modal="true"
      onTransitionEnd={handleTransitionEnd}
    >
      <Row className="shrink-0 items-center justify-between px-2">
        <Box href="/" aria-label="Go to homepage" analyticsName="cosmos-mobile-menu-home">
          <OpendoorLogo width="96px" />
        </Box>
        <Box
          onClick={onClose}
          aria-label="Close menu"
          className="inline-flex size-11 items-center justify-center rounded-[10px] border-0 bg-[#ede8e8] p-0 text-od-content-primary"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
            <path
              d="m4 4 8 8M12 4l-8 8"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
        </Box>
      </Row>
      {/* eslint-disable-next-line @opendoor/no-html-elements -- Atoms has no list primitive; <ul> preserves list semantics */}
      <ul className={cn(styles.menuList, 'relative block shrink-0')}>
        {megaNavData ? (
          <>
            <Col
              className={cn(
                'transition-transform duration-300 ease-in-out',
                showSubMenu ? 'absolute inset-0 -translate-x-full' : 'relative translate-x-0',
              )}
            >
              {mainMenu}
            </Col>
            {activeSubMenu && (
              <Col
                className={cn(
                  'transition-transform duration-300 ease-in-out',
                  showSubMenu ? 'relative translate-x-0' : 'absolute inset-0 translate-x-full',
                )}
              >
                <MobileMenuSubMenu
                  megaNavData={megaNavData}
                  openSubMenu={activeSubMenu}
                  onBack={() => setOpenSubMenu(null)}
                  footer={menuFooter}
                />
              </Col>
            )}
          </>
        ) : (
          mainMenu
        )}
      </ul>
    </Box>
  );
};
